import { getClientId } from './client-id';
import { DeviceInfoDto } from './dto';
import { detectIncognito } from './incognito';

interface UserAgentData {
	mobile?: boolean;
	platform?: string;
	getHighEntropyValues?: (hints: string[]) => Promise<{
		platform?: string;
		platformVersion?: string;
		model?: string;
		uaFullVersion?: string;
	}>;
}

interface NetworkInformation {
	type?: string;
	effectiveType?: string;
}

interface CollectedUserAgentData {
	platform: string | null;
	platform_version: string | null;
	model: string | null;
	browser_version: string | null;
	is_mobile: boolean | null;
}

/**
 * Точная версия ОС и модель устройства доступны только через User-Agent Client Hints:
 * в самом User-Agent эти данные урезаны (например, Windows 10 и 11 неразличимы).
 * Поддерживается только браузерами на Chromium.
 */
async function getUserAgentData(): Promise<CollectedUserAgentData | null> {
	const uaData = (navigator as Navigator & { userAgentData?: UserAgentData }).userAgentData;
	if (!uaData) return null;

	const base: CollectedUserAgentData = {
		platform: uaData.platform ?? null,
		platform_version: null,
		model: null,
		browser_version: null,
		is_mobile: uaData.mobile ?? null,
	};

	try {
		const values = uaData.getHighEntropyValues
			? await uaData.getHighEntropyValues(['platform', 'platformVersion', 'model', 'uaFullVersion'])
			: {};
		return {
			...base,
			platform: values.platform ?? base.platform,
			platform_version: values.platformVersion ?? null,
			model: values.model || null,
			browser_version: values.uaFullVersion ?? null,
		};
	} catch {
		return base;
	}
}

/**
 * Видеоадаптер: устойчивый признак железа, одинаковый в разных браузерах одного устройства
 */
function getGpu(): string | null {
	try {
		const canvas = document.createElement('canvas');
		const gl = (canvas.getContext('webgl') ?? canvas.getContext('experimental-webgl')) as WebGLRenderingContext;
		if (!gl) return null;

		const info = gl.getExtension('WEBGL_debug_renderer_info');
		if (!info) return null;

		const vendor = gl.getParameter(info.UNMASKED_VENDOR_WEBGL);
		const renderer = gl.getParameter(info.UNMASKED_RENDERER_WEBGL);
		const value = [vendor, renderer].filter(Boolean).join(' / ');
		return value ? value.slice(0, 256) : null;
	} catch {
		return null;
	}
}

function getTimezone(): string | null {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
	} catch {
		return null;
	}
}

/**
 * Тип сети отдает только Network Information API (Chromium).
 * В Safari и Firefox признак недоступен - там остается IP адрес, который пишется на сервере.
 */
function getNetwork() {
	const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection;
	return {
		network_type: connection?.type ?? null,
		network_effective_type: connection?.effectiveType ?? null,
	};
}

/**
 * Сбор признаков устройства сотрудника для журнала отметок
 */
export async function collectDeviceInfo(): Promise<DeviceInfoDto> {
	const [clientId, incognito, uaData] = await Promise.all([getClientId(), detectIncognito(), getUserAgentData()]);
	const navigatorInfo = navigator as Navigator & { deviceMemory?: number };

	return {
		client_id: clientId.client_id,
		has_persistent_storage: clientId.has_persistent_storage,

		platform: uaData?.platform ?? null,
		platform_version: uaData?.platform_version ?? null,
		model: uaData?.model ?? null,
		browser_version: uaData?.browser_version ?? null,
		is_mobile: uaData?.is_mobile ?? null,

		screen_width: window.screen?.width ?? null,
		screen_height: window.screen?.height ?? null,
		pixel_ratio: window.devicePixelRatio ?? null,
		color_depth: window.screen?.colorDepth ?? null,

		timezone: getTimezone(),
		language: navigator.language ?? null,
		hardware_concurrency: navigator.hardwareConcurrency ?? null,
		device_memory: navigatorInfo.deviceMemory ?? null,
		max_touch_points: navigator.maxTouchPoints ?? null,
		gpu: getGpu(),

		...getNetwork(),

		is_incognito: incognito.is_incognito,
		incognito_reason: incognito.incognito_reason,
	};
}
